import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import { Briefcase, DotsThree, PencilSimpleLine, Trash } from "@phosphor-icons/react";
import UpdateChatTitleModal from "src/pages/Modals/UpdateThreadTitleModal";
import { setThreadTitleTop } from "src/store/slices/messageSlice";
import { trackAnalytics } from "src/pages/helpers.js";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants.js";
import { convertDateTime } from "./helpers.js";

const ThreadListItem = ({
  thread,
  index,
  duration,
  activeIndex,
  setActiveIndex,
  renameActiveId,
  setRenameActiveId,
  actionOnThread,
  setActionOnThread,
  updateThreadInfo,
  updateActionLoading,
  setUpdateActionLoading,
  onPerformThreadAction,
  closeChatHistory,
  dropdownClicked,
  hoveredThredId,
  setHoveredThreadId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lawyerHubData = useSelector((state) => state.onboarding.lawyerHubData);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);

  const dropdownItems = [
    {
      key: "rename",
      label: (
        <div
          data-testid="thread-rename-option"
          className="flex flex-row gap-[8px] px-2 py-1"
          onClick={(e) => onPerformThreadAction(thread, "Rename", duration, e)}
        >
          <PencilSimpleLine size={16} className="my-auto text-[#B6B6B6]" />
          <span className="text-[#353535] my-auto text-[14px] leading-[12px]">Rename</span>
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          data-testid="thread-delete-option"
          className="flex flex-row gap-[8px] px-2 py-1"
          onClick={(e) => onPerformThreadAction(thread, "Delete", duration, e)}
        >
          <Trash className="my-auto text-[#A20009]" />
          <span className="text-[#A20009] my-auto text-[14px] leading-[12px]">Delete</span>
        </div>
      ),
    },
  ].filter(Boolean);

  const handleThreadClick = () => {
    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.THREAD_ID]: thread?.id,
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
        [EVENT_PARAMETERS.BUTTON_TEXT]: "View Thread",
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CHAT_HISTORY,
      },
      userMetadata,
    );
    dispatch(setThreadTitleTop({ threadId: thread.id, title: thread.title }));
    navigate(`/chat/${thread.id}`);
    closeChatHistory();
  };

  const getHoverClass = () => {
    if (thread.type === "ask" || thread.type === "draft" || thread.type === "review") {
      return "group-hover:bg-[#FAFAFA]";
    }
    return "";
  };

  const getRenameActiveClass = () => {
    if (renameActiveId === thread.id) {
      return "bg-[#FAFAFA]";
    }
    return "";
  };

  return (
    <li key={thread.id} data-testid="thread-list-item" className="bg-white group" onMouseLeave={() => setActiveIndex()}>
      <div
        className={`rounded-[8px] chat-message w-full flex items-center justify-between p-[12px] gap-[8px] ${getHoverClass()} ${getRenameActiveClass()}`}
      >
        <div
          onClick={() => {
            if (renameActiveId !== thread.id) {
              closeChatHistory();
              dispatch(setThreadTitleTop({ threadId: thread.id, title: thread.title }));
              navigate(`/chat/${thread.id}`);
            }
          }}
          className={`chat-user flex flex-row gap-[8px] w-full ${renameActiveId === thread.id ? "cursor-default" : "cursor-pointer"}`}
        >
          {renameActiveId === thread.id ? (
            <UpdateChatTitleModal
              open={actionOnThread === "Rename"}
              setActionOnThread={setActionOnThread}
              threadInfo={updateThreadInfo}
              loading={updateActionLoading}
              setLoading={setUpdateActionLoading}
              setRenameActiveId={setRenameActiveId}
            />
          ) : (
            <div className="">
              <div
                className="flex gap-[8px] cursor-pointer text-[14px] font-sans font-medium text-[#353535]"
                onClick={handleThreadClick}
              >
                {thread.title}
                {lawyerHubData?.some((item) => item.thread_id === thread.id && item.finalization_requested) && (
                  <div
                    className="relative"
                    onMouseEnter={() => setHoveredThreadId(thread.id)}
                    onMouseLeave={() => setHoveredThreadId(null)}
                  >
                    <span data-testid="thread-finalization-badge" className="rounded-[4px] flex justify-center items-center w-[31px] h-[23px] bg-[#FBF6E8]">
                      <Briefcase size={15} className="text-[#DCA218]" />
                    </span>
                    {hoveredThredId === thread?.id && (
                      <div className="absolute z-[1000] -bottom-9 left-1/2 -translate-x-1/2 flex bg-[#011513] text-white w-[280px] rounded-[8px] items-center justify-center">
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-0 border-b-4 border-transparent border-b-[#011513]"></div>
                        <span className="text-[11px] font-[400] font-sans px-4 py-2">
                          Document Finalization has been requested on a document in this chat
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-[6px]">
                <p className="text-[10px] font-normal font-sans leading-[12px] text-[#686868]">
                  {convertDateTime(thread.created_at)}
                </p>
                {thread.is_privileged === true && (
                  <span className="shrink-0 inline-flex items-center gap-[8px] px-[8px] py-[4px] rounded-[99px] bg-[#F2F4FF] text-[#0027FF]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 5V3.5C9 2.67 9.67 2 10.5 2H13.5C14.33 2 15 2.67 15 3.5V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 9C3 7.9 3.9 7 5 7H19C20.1 7 21 7.9 21 9V11C21 11 17 13 12 13C7 13 3 11 3 11V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 11V18C3 19.1 3.9 20 5 20H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 11V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 19L18 21L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-[12px] font-normal font-sans text-[#0027FF] leading-[120%]">Privileged</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        {renameActiveId !== thread.id && (
          <div className="hidden md:block">
            <div className="rounded-[60px] w-[24px] h-[24px] z-0 mx-auto my-auto transform bg-white hidden shadow group-hover:flex cursor-pointer transition-display duration-300">
              <Dropdown
                open={activeIndex === index}
                onOpenChange={(visible) => setActiveIndex(visible ? index : null)}
                trigger={["click"]}
                placement="bottomRight"
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                menu={{ items: dropdownItems }}
              >
                <p
                  data-testid="thread-menu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    dropdownClicked(index);
                  }}
                  className="relative mx-auto my-auto text-black cursor-pointer"
                >
                  <DotsThree weight="bold" size={16} />
                </p>
              </Dropdown>
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

export default ThreadListItem;
